import React, { useState, useEffect, useCallback } from "react";
import { updateClientData } from "services/customer";
import { toast } from "react-toastify";
import { dailyIntakeAndRecallRow } from "containers/clientDetails/sub-components/common/dailyIntakeAndRecallTableRows";
import SwipableModal from "containers/Customer/modal";
import DailyIntakeModal from "containers/clientDetails/sub-components/modal/dailyIntake";
import { dailyIntakeAndRecallTableColumns } from "utils/tablesHeaderContent";
import { timestampConversion } from "utils/common";
import moment from "moment";
import ClientInfoCard from "containers/clientDetails/sub-components/card/clientCard";
import { headerDesc } from "utils/helper";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

function DailyIntakeForm({ clientId, dailyIntakeArray, deleteHandler }) {
  const [dailyIntakeData, setDailyIntakeData] = useState([]);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([...dailyIntakeAndRecallTableColumns]);
  const [disableButton, setDisableButton] = useState(true);
  const [selectedData, setSelectedData] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [loader, setLoader] = useState(false);
  const [mode, setMode] = useState("create");

  useEffect(() => {
    if (dailyIntakeArray.length === 0) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
    setDailyIntakeData(dailyIntakeArray);
  }, [dailyIntakeArray]);

  const editHandler = useCallback((data) => {
    setSelectedData(data);
    setMode("edit");
    setOpenAddModal(true);
  }, []);

  const viewHandler = useCallback((data) => {
    setSelectedData(data);
    setMode("view");
    setOpenAddModal(true);
  }, []);

  //sorting according to date
  //and listing daily intake data in table
  useEffect(() => {
    const convertTimestamp = (timestamp) => {
      return timestamp?.seconds
        ? moment(timestampConversion(timestamp))
        : moment(timestamp);
    };
    let dailyIntakeFoodArray = dailyIntakeData.slice();
    dailyIntakeFoodArray.sort((a, b) => {
      const a_timestamp = convertTimestamp(a?.timestamp);
      const b_timestamp = convertTimestamp(b?.timestamp);
      return b_timestamp - a_timestamp;
    });
    setRows(
      dailyIntakeAndRecallRow(
        dailyIntakeFoodArray,
        editHandler,
        viewHandler,
        deleteHandler,
        "dailyIntake"
      )
    );
  }, [dailyIntakeData, editHandler, viewHandler]);

  const addHandler = () => {
    setMode("create");
    setOpenAddModal(true);
  };

  const handleCloseModal = () => {
    setOpenAddModal(false);
    setSelectedData(null);
  };

  const submitHandler = async () => {
    try {
      setLoader(true);

      // Helper function to upload images
      const uploadImageIfExists = async (imageFile, path) => {
        if (!imageFile || !path) {
          console.error("Invalid image file or path:", { imageFile, path });
          throw new Error("Invalid image file or path");
        }

        const storage = getStorage();
        const imageRef = ref(storage, path);
        await uploadBytes(imageRef, imageFile);
        return await getDownloadURL(imageRef);
      };

      // Process dailyIntakeData to handle image uploads
      const processedData = await Promise.all(
        dailyIntakeData.map(async (item) => {
          const updatedItem = { ...item };

          for (let key in updatedItem) {
            const section = updatedItem[key];
            if (section && typeof section === "object" && section.image instanceof File) {
              const file = section.image;

              // Validate file name and key before constructing the path
              if (!file.name || !key) {
                console.error("Invalid file name or key:", { file, key });
                throw new Error("Invalid file name or key");
              }

              const imageUrl = await uploadImageIfExists(
                file,
                `dailyIntake/${clientId}/${key}/${file.name}`
              );
              updatedItem[key].image = imageUrl; // Replace File object with URL
            }
          }

          return updatedItem;
        })
      );

      // Send processed data to the backend
      const fieldKey = "dailyIntake";
      await updateClientData(clientId, fieldKey, processedData);
      setLoader(false);
      setDisableButton(true);
      toast.success("Data Added successfully");
    } catch (error) {
      setLoader(false);
      console.error("Error in submitHandler:", error);
      toast.error("Something went wrong!");
    }
  };

  const Modal = () => (
    <SwipableModal
      open={openAddModal}
      setOpenModal={setOpenAddModal}
      handleCloseModal={handleCloseModal}
      mode={mode}
      setMode={setMode}
      {...headerDesc(mode, "Client's Daily Intake", selectedData)}
    >
      <DailyIntakeModal
        id={clientId}
        mode={mode}
        persona="client"
        open={openAddModal}
        selectedItem={selectedData}
        setMainData={setDailyIntakeData}
        handleCloseModal={handleCloseModal}
      />
    </SwipableModal>
  );

  return (
    <ClientInfoCard
      title={"Client's Daily Intake"}
      rows={rows}
      columns={columns}
      disableButton={disableButton}
      onSubmitHandler={submitHandler}
      loader={loader}
      onBtnClickHandler={addHandler}
      Modal={Modal}
    />
  );
}

export default DailyIntakeForm;
